import { Float, type ISplittedNumberText, rxFixPointNumber, rxIntegerNumber } from "./settings.ts";
import { splitNumberParts } from "./splitNumberParts.ts";

/*
 * Is  x  within Floats implementation limits?
 *  Test if x is a finite number in range of the implementation limits of Float
 *  x should be able to pass all layers of our application down to the ORACLE tables
 *  and back from the tables back to the UI.
 *  If abs(x) is smaller than Float.MIN_VALUE it is rounded to zero (not tested).
 *  @param x: number  - A JAvaScript Number == Double
 *  @return true if x is finite and within -Float.MAX_VALUE ... Float.MAX_VALUE
 */
function inRangeOfFloat(x: number): boolean {
  return Number.isFinite(x) && Math.abs(x) <= Float.MAX_VALUE;
}

/*
 * Is  x  indistinguishable from zero for a Float?
 *
 *  @param x: number  - A JavaScript Number == Double
 *  @return true if x is finite and within -Float.MAX_VALUE[ ... [Float.MAX_VALUE
 */
export function isFloatZero(x: number): boolean {
  return Number.isFinite(x) && x !== 0 && Math.abs(x) < Float.MIN_VALUE;
}

/**
 * Normalize exponent and mantissa of the float in text to a number representation with one non-zero digit
 * in front of the fraction separator (dot).
 * Adjusts the exponent according to the shift of the mantissa.
 * Absolute value of mantissa will be in range   [1 ... [10.
 * Exponent can be outside range [-38 ... +38]!
 *  @param text The number to normalize
 */
function normalizeFloat32(text: string): ISplittedNumberText {
  const parts: ISplittedNumberText = splitNumberParts(text);
  //  'e1'  == 1e1 => (m,e) = (1, 1)
  //  '0e1' == 0   => (m,e) = (0, 0)
  let e: number = !!parts.exponent && rxIntegerNumber.test(parts.exponent) ? Number(parts.exponent) : 0;
  let m: number = !!parts.mantissa && rxFixPointNumber.test(parts.mantissa) ? Number(parts.mantissa) : e ? 1 : 0;

  if (m === 0) {
    //  '0eX'  ==  0  => (m,e) = (0, e-not-set )
    return { mantissa: "0" };
  } else {
    // shift the mantissa and exponent until mantissa is in range of [1...[10
    while (Math.abs(m) < 1) {
      m *= 10;
      e -= 1;
    }
    while (Math.abs(m) >= 10) {
      m *= 0.1;
      e += 1;
    }

    const r: ISplittedNumberText = {
      mantissa: "" + m,
    };

    if (e !== 0 || !!parts.exponent) {
      r.exponent = "" + e;
    }

    return r;
  }
}

/**
 * Test if a 32-bit Float can represent the number value without using Inf and NaN.
 * Applies the same rules as checkIsTextOfAFloat32.
 *  @parameter x - the number text to test
 *  @return   true if the number is representable in a 32-bit Float
 */
export function isTextOfAFloat32(text: string): boolean {
  const ret = checkIsTextOfAFloat32(text);

  return !!ret?.message;
}

interface ICheckIsTextOfAFloat32ReturnValue {
  code: number;
  message: string;
}

/**
 * Test if a 32-bit Float can represent the number value without using Inf and NaN.
 * For valid text it returns undefined.
 * For invalid text it returns a diagnostic message and code in a structure.
 * Some valid finite numbers are not storable in Oracle or not parse-able by Java when read back from Oracle.
 *   * null or undefined: => rejected
 *   * zero : => accepted
 *   * if x is not a finite java script number (or NaN)    => rejected
 *  Rejected number-text:
 *   * numbers close to zero: abs(x) in the gap from 0..1e-38 => rejected
 *   * numbers outside the range of +-Float.MAX_VALUE.     => rejected (not finite for Float32)
 *   * other numbers => accepted
 *  @parameter text - the number text to test
 *  @return   { message, code } - undefined or a structure with a diagnostic message and a diag code
 */
export function checkIsTextOfAFloat32(text?: string | null): ICheckIsTextOfAFloat32ReturnValue | null {
  if (text == null) {
    return { code: 1, message: "No value" };
  }

  const parts = normalizeFloat32(text);
  if (!parts.mantissa) {
    return { code: 10, message: "Value has no mantissa value" };
  }

  if (!rxFixPointNumber.test(parts.mantissa)) {
    return { code: 11, message: "Invalid format of mantissa" };
  }

  if (parts.mantissa.length > 20) {
    return { code: 12, message: "Mantissa longer than 20 characters" };
  }

  if (parts.exponent) {
    if (!rxIntegerNumber.test(parts.exponent)) {
      return { code: 21, message: "Exponent is not an integer" };
    }
    if (Math.abs(Number.parseInt(parts.exponent, 10)) > 38) {
      return { code: 21, message: "Exponent is outside of range -38 .. +38" };
    }
  }

  try {
    const n = Number(text);
    if (Number.isNaN(n)) {
      return { code: 31, message: "NaN is not a valid value" };
    }

    if (!Number.isFinite(n)) {
      return { code: 32, message: "Value is not a finite number" };
    }

    if (!inRangeOfFloat(n)) {
      return {
        code: n > 0 ? 33 : 34,
        message: `Value must be ${n > 0 ? "smaller" : "larger"} than ${n > 0 ? Float.MAX_VALUE : -1 * Float.MAX_VALUE}`,
      };
    }

    if (isFloatZero(n)) {
      return { code: 35, message: `Use zero instead of values in range [-${Float.MIN_VALUE}..${Float.MIN_VALUE}]` };
    }
  } catch {
    return { code: 30, message: "Cannot convert to a number" };
  }

  return null;
}
